import React, { useState, useEffect, useCallback } from 'react';
import { BottomNav } from '../components/BottomNav';
import { AppHeader } from '../components/AppHeader';
import { apiFetch } from '../services/api';
import type { AuthUser } from '../hooks/useAuth';

interface Props { user: AuthUser; }

interface RestRequest {
  id: string;
  request_type: string;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  status: string;
  review_comment: string | null;
  reviewed_at: string | null;
  created_at: string;
}

const STATUS_META: Record<string, { label: string; color: string; bg: string; border: string }> = {
  pending:   { label: '承認待ち', color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  approved:  { label: '承認済み', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  rejected:  { label: '却下',     color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  cancelled: { label: '取り下げ', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
};

function todayYmd(): string {
  return new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
}
function dayCount(a: string, b: string): number {
  if (!a || !b) return 0;
  const s = new Date(a + 'T00:00:00Z').getTime();
  const e = new Date(b + 'T00:00:00Z').getTime();
  if (isNaN(s) || isNaN(e) || e < s) return 0;
  return Math.round((e - s) / 86400000) + 1;
}

const inputStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc',
  padding: '11px 12px', fontSize: 15, width: '100%', minWidth: 0, outline: 'none', boxSizing: 'border-box', color: '#0f172a',
};

const LeaveRequestPage: React.FC<Props> = ({ user: _user }) => {
  const [reqType, setReqType] = useState<'desired' | 'shore_work'>('desired');
  const [start, setStart] = useState(todayYmd());
  const [end, setEnd] = useState(todayYmd());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [list, setList] = useState<RestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), type === 'error' ? 6000 : 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<{ data: RestRequest[] }>('/leave/rest-requests');
      setList(r.data ?? []);
    } catch { setList([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const days = dayCount(start, end);

  const submit = async () => {
    if (!start || !end) { showToast('開始日・終了日を入力してください', 'error'); return; }
    if (end < start) { showToast('終了日は開始日以降にしてください', 'error'); return; }
    setSubmitting(true);
    try {
      await apiFetch('/leave/rest-requests', {
        method: 'POST',
        body: JSON.stringify({ request_type: reqType, start_date: start, end_date: end, reason: reason.trim() || undefined }),
      });
      showToast(reqType === 'shore_work' ? '陸上勤務を申請しました' : '希望休を申請しました');
      setReason('');
      load();
    } catch (e: any) {
      showToast(e?.message ?? '申請に失敗しました', 'error');
    } finally { setSubmitting(false); }
  };

  const cancel = async (id: string) => {
    if (!window.confirm('この希望休を取り下げますか？')) return;
    try { await apiFetch(`/leave/rest-requests/${id}`, { method: 'DELETE' }); load(); } catch { /* ignore */ }
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f8fafc' }}>
      <AppHeader title="休暇・希望休" />

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* 申請フォーム */}
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: 18 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>申請</div>
          {/* 種別: 希望休 / 陸上勤務 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {([['desired', '🌴 希望休', '休みたい日を申請'], ['shore_work', '🏢 陸上勤務', '陸上で勤務する日を申請']] as const).map(([key, label, desc]) => {
              const on = reqType === key;
              return (
                <button key={key} onClick={() => setReqType(key)}
                  style={{ padding: '12px 10px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                    border: on ? '2px solid #0057d9' : '1.5px solid #e4e9f0', background: on ? '#eff6ff' : 'white' }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: on ? '#0057d9' : '#1e293b' }}>{label}</div>
                  <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 2 }}>{desc}</div>
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
            {reqType === 'shore_work'
              ? '陸上で勤務する日を申請します。承認されると「休暇」には数えられません（勤務扱い）。'
              : '希望する休みを申請します。陸上管理者の承認後に確定し、結果は連絡（メッセージ）に届きます。'}
          </div>
          {/* minmax(0,1fr): date入力は固有の最小幅を持つので、1frのままだとスマホ幅で終了日が画面外へはみ出す */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 10, marginBottom: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>開始日</span>
              <input type="date" value={start} min={todayYmd()} onChange={e => setStart(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>終了日</span>
              <input type="date" value={end} min={start || todayYmd()} onChange={e => setEnd(e.target.value)} style={inputStyle} />
            </div>
          </div>
          {days > 0 && (
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#1e40af', fontWeight: 700 }}>
              {days}日間の{reqType === 'shore_work' ? '陸上勤務' : '希望休'}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>理由（任意）</span>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              placeholder="例：家族の用事のため" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <button onClick={submit} disabled={submitting}
            style={{ width: '100%', padding: '14px', borderRadius: 14, border: 'none',
              background: submitting ? '#93c5fd' : 'linear-gradient(145deg,#0057d9,#0369a1)',
              color: 'white', fontSize: 15, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer' }}>
            {submitting ? '申請中...' : reqType === 'shore_work' ? '陸上勤務を申請する' : '希望休を申請する'}
          </button>
        </div>

        {/* 申請状況 */}
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#475569', margin: '4px 4px 10px' }}>申請状況</div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>読み込み中...</div>
          ) : list.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8', fontSize: 14 }}>まだ申請はありません</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {list.map(r => {
                const st = STATUS_META[r.status] ?? STATUS_META['pending']!;
                return (
                  <div key={r.id} style={{ background: 'white', borderRadius: 14, border: '1px solid #e2e8f0', padding: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', flex: 1 }}>
                        {r.start_date.slice(5)} 〜 {r.end_date.slice(5)}
                        <span style={{ fontWeight: 500, color: '#64748b', marginLeft: 8, fontSize: 12 }}>{r.days}日</span>
                        {r.request_type === 'shore_work' && <span style={{ fontSize: 10, marginLeft: 6, color: '#0369a1', fontWeight: 700 }}>🏢陸上勤務</span>}
                        {r.request_type === 'leave' && <span style={{ fontSize: 10, marginLeft: 6, color: '#16a34a', fontWeight: 700 }}>（実績登録）</span>}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>{st.label}</span>
                    </div>
                    {(r.reason || r.review_comment) && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                        {r.reason && <div>理由: {r.reason}</div>}
                        {r.review_comment && <div style={{ color: st.color }}>管理者: {r.review_comment}</div>}
                      </div>
                    )}
                    {r.status === 'pending' && (
                      <div style={{ marginTop: 10, textAlign: 'right' }}>
                        <button onClick={() => cancel(r.id)}
                          style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', border: '1px solid #fecaca', borderRadius: 10, background: 'white', color: '#dc2626', cursor: 'pointer' }}>
                          取り下げる
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          maxWidth: 340, padding: '12px 20px', borderRadius: 24,
          background: toast.type === 'error' ? '#dc2626' : '#0f172a',
          color: 'white', fontSize: 13, fontWeight: 600, textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.25)' }}>
          {toast.msg}
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default LeaveRequestPage;
